import Alpine from 'alpinejs';
import instantsearch from 'instantsearch.js';
import { instantMeiliSearch } from '@meilisearch/instant-meilisearch';
import { searchBox, hits } from 'instantsearch.js/es/widgets';

const escapeHtml = (value) =>
    String(value || '').replace(
        /[&<>"']/g,
        (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );

const MARK_OPEN = '___SEARCH_MARK_OPEN___';
const MARK_CLOSE = '___SEARCH_MARK_CLOSE___';

const NAMED_ENTITIES = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    apos: "'",
    nbsp: ' ',
    shy: '',
};

// Inline editing stores titles and rich text HTML-encoded, and the index keeps that
// verbatim. Decoding is safe because every caller escapes afterwards; it is done
// without the DOM so no markup can be reintroduced here.
const decodeEntities = (value) =>
    String(value || '').replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (entity, reference) => {
        if (reference[0] !== '#') {
            const named = NAMED_ENTITIES[reference.toLowerCase()];
            return named === undefined ? entity : named;
        }

        const codePoint =
            reference[1] === 'x' || reference[1] === 'X'
                ? parseInt(reference.slice(2), 16)
                : parseInt(reference.slice(1), 10);

        if (!Number.isFinite(codePoint) || codePoint < 32 || codePoint > 0x10ffff) {
            return entity;
        }

        try {
            return String.fromCodePoint(codePoint);
        } catch (error) {
            return entity;
        }
    });

const normalizeText = (value, preserveMarks = false) => {
    let text = String(value || '');

    if (preserveMarks) {
        text = text.replace(/<mark>/gi, MARK_OPEN).replace(/<\/mark>/gi, MARK_CLOSE);
    }

    text = decodeEntities(
        text
            .replace(/<\/?(br|p|div|li|h[1-6])\b[^>]*>/gi, ' ')
            // Inline tags join what they split: inline editing leaves words like
            // "Öster</span><span>reich" in stored text.
            .replace(/<\/?[a-z][^>]*>/gi, '')
            // A snippet crop can end inside a tag, which a browser drops, or inside an
            // entity such as "&nbsp…", which a browser still resolves.
            .replace(/<\/?[a-z][^>]*$/i, '')
            .replace(/&(#x?[0-9a-f]+|amp|lt|gt|quot|apos|nbsp|shy)(?=[^a-z0-9;]*$)/i, '&$1;')
    )
        .replace(/\s+/g, ' ')
        .trim();

    if (preserveMarks) {
        text = text.replace(new RegExp(MARK_OPEN, 'g'), '<mark>').replace(new RegExp(MARK_CLOSE, 'g'), '</mark>');
    }

    return text;
};

// Hits are rendered from an HTML string: only the <mark> highlighting may survive as markup.
const allowMarkTags = (value) =>
    escapeHtml(normalizeText(value, true))
        .replace(/&lt;mark&gt;/g, '<mark>')
        .replace(/&lt;\/mark&gt;/g, '</mark>');

const stripTags = (value) => normalizeText(value);

// InstantSearch HTML-escapes highlight and snippet values before wrapping matches in
// <mark>; undo that once so they read like the stored attributes.
const highlighted = (result) => (result?.value && result.value !== 'null' ? decodeEntities(result.value) : '');

Alpine.data(
    'searchPopup',
    ({
        host = '',
        apiKey = '',
        testMode = false,
        indexName = 'neos',
        searchBoxPlaceholderLabel = 'Search…',
        noResultsLabel = 'No results',
    } = {}) => ({
        host,
        apiKey,
        testMode,
        indexName,
        searchBoxPlaceholderLabel: searchBoxPlaceholderLabel || 'Search…',
        noResultsLabel: noResultsLabel || 'No results',
        showSearch: false,
        search: null,
        searchQuery: '',

        init() {
            if (!this.host || !this.apiKey) {
                console.warn('[SearchPopup] Search initialization skipped: host and apiKey are required.');
            }
        },

        // Sets up a fresh InstantSearch instance (client, widgets, listeners). Called when opening the modal.
        initializeSearch() {
            let searchClient;
            if (this.testMode) {
                // Use a mock client in styleguide/test mode fetching static results
                const mockFakeMatchForValue = (obj, searchTerm) => {
                    const regex = new RegExp(`(${searchTerm})`, 'gi');
                    if (obj?.value) {
                        obj.value = obj.value
                            .replace('__ais-highlight__Honda__/ais-highlight__', 'Honda')
                            .replace(regex, '__ais-highlight__$1__/ais-highlight__');
                    }
                };
                searchClient = {
                    search: async (requests) => {
                        const searchTerm = requests[0].params.query;
                        const response = await fetch(this.host);
                        const json = await response.json();
                        json.results[0].hits = json.results[0].hits.filter((hit) => {
                            return hit._highlightResult?.title?.value.toLowerCase().includes(searchTerm.toLowerCase());
                        });
                        let l = json.results[0].hits.length;
                        json.results[0].hits.forEach((hit) => {
                            mockFakeMatchForValue(hit._highlightResult?.title, searchTerm);
                            mockFakeMatchForValue(hit._snippetResult?.__fulltext?.h1, searchTerm);
                            mockFakeMatchForValue(hit._snippetResult?.__fulltext?.h2, searchTerm);
                            mockFakeMatchForValue(hit._snippetResult?.__fulltext?.text, searchTerm);

                            let hasTitle = true;

                            // fix local image paths
                            let imageId = getRandomInt(0, 100);
                            hit.menuImage = `https://picsum.photos/id/${imageId}/200/300`;
                            hit.heroImage = `https://picsum.photos/id/${imageId}/200/300`;
                            hit.image = `https://picsum.photos/id/${imageId}/200/300`;

                            // no image (33% without image)
                            if (getRandomInt(0, l) <= l * 0.33) {
                                hit.heroImage = hit.menuImage = hit.image = null;
                            }

                            // no title (33% without title)
                            if (getRandomInt(0, l) <= l * 0.33) {
                                hit.title = null;
                                hit._highlightResult = null;
                                hasTitle = false;
                            }

                            // no text (33% without text and only if title exist, otherwise no text to search)
                            if (getRandomInt(0, l) <= l * 0.33 && hasTitle) {
                                hit._snippetResult = null;
                                hit.metaDescription = null;
                                hit.__fulltext = null;
                            }
                        });

                        function getRandomInt(min, max) {
                            // Ensure min and max are integers (optional, but good practice)
                            min = Math.ceil(min);
                            max = Math.floor(max);
                            // The formula ensures both min and max are possible results
                            return Math.floor(Math.random() * (max - min + 1)) + min;
                        }

                        return json;
                    },
                };
            } else {
                // Use Meilisearch client in normal mode
                const meiliSearchResult = instantMeiliSearch(this.host, this.apiKey, {
                    placeholderSearch: false,
                    primaryKey: 'id',
                });
                // The instantMeiliSearch function returns the search client directly, not an object with a searchClient property
                searchClient = meiliSearchResult?.searchClient || meiliSearchResult;
            }

            this.search = instantsearch({
                indexName: this.indexName,
                searchClient,
                // Prevent initial/empty searches; only search after a non-empty query is submitted
                searchFunction: (helper) => {
                    const q = (helper.state.query || '').trim();
                    // Keep local state in sync with helper
                    this.searchQuery = q;
                    if (q.length >= 3) {
                        helper.search();
                    }
                },
            });

            const noResultsText = this.noResultsLabel;

            this.search.addWidgets([
                searchBox({
                    container: this.$refs.searchbox,
                    placeholder: this.searchBoxPlaceholderLabel,
                    searchAsYouType: true,
                    showLoadingIndicator: true,
                    autofocus: true,
                    cssClasses: {
                        form: 'flex gap-2',
                    },
                }),
                hits({
                    container: this.$refs.searchhits,
                    // A document whose URI could not be resolved at index time would render as a result linking nowhere.
                    transformItems: (items) => items.filter((item) => item.__uri),
                    templates: {
                        item(hit) {
                            const title =
                                highlighted(hit._highlightResult?.titleOverride) ||
                                hit.titleOverride ||
                                highlighted(hit._highlightResult?.title) ||
                                hit.title ||
                                '';
                            const titleHtml = allowMarkTags(title);
                            const url = hit.__uri;

                            // Snippet selection from _snippetResult.__fulltext with priority: text > h2 > h1
                            let snippet = '';
                            const snippetFields = ['text', 'h2', 'h1'];
                            const cleanTitle = stripTags(hit.title);
                            for (const field of snippetFields) {
                                const candidateContent = highlighted(hit?._snippetResult?.__fulltext?.[field]);
                                if (candidateContent.includes('<mark>') && stripTags(candidateContent) !== cleanTitle) {
                                    snippet = candidateContent;
                                    break;
                                }
                            }

                            if (!snippet) {
                                // fallbacks
                                snippet =
                                    hit.metaDescription?.value !== 'null'
                                        ? hit.metaDescription?.value
                                        : hit.__fulltext?.text;
                            }
                            const snippetHtml = allowMarkTags(snippet);

                            let imageUri = hit.image || hit.menuImage || hit.heroImage || '';
                            let isImage = !!imageUri;

                            let imageNode = `
                                <div class="flex-shrink-0 w-32 min-h-[96px]">
                                    <img src="${escapeHtml(imageUri)}"
                                        alt="${escapeHtml(stripTags(title))}"
                                        class="bg-gray-200 object-cover rounded aspect-[4/3] w-full min-h-[96px]"/>
                                </div>`;
                            let textNode = `
                                    <div class="flex-1 flex flex-col justify-center">
                                        ${
                                            titleHtml
                                                ? `<div class="font-semibold text-gray-900 text-18 [view-transition-name:hero-title]">${titleHtml}</div>`
                                                : ''
                                        }
                                        ${
                                            snippetHtml
                                                ? `<div class="text-sm text-gray-600 ${
                                                      titleHtml ? 'line-clamp-3' : 'line-clamp-4'
                                                  }">${snippetHtml}</div>`
                                                : ''
                                        }
                                    </div>`;

                            return `<a href="${escapeHtml(
                                url
                            )}" class="flex gap-3 p-3 rounded hover:bg-gray-100 [view-transition-name:hero] word-break-word xs:break-normal">
                                        ${isImage ? imageNode : ''}
                                        ${textNode}
                                    </a>`;
                        },
                        empty() {
                            return `<div class="p-3 grid gap-3 px-4 pb-4 xs:py-0 opacity-75">${noResultsText}</div>`;
                        },
                    },
                }),
            ]);

            this.search.start();

            // Keep Alpine state in sync on each render (covers cases beyond searchFunction)
            this.search.on('render', () => {
                try {
                    this.searchQuery = (this.search?.helper?.state?.query || '').trim();
                } catch (e) {
                    // no-op
                }
            });
        },

        openModal() {
            this.showSearch = true;
            document.body.classList.add('body--is-locked');

            // Initialize InstantSearch on demand (avoid re-initialization if already open)
            if (!this.search) {
                this.initializeSearch();
            }

            // Focus the search input without triggering a search
            setTimeout(() => {
                queueMicrotask(() => {
                    const input = this.$refs.searchbox?.querySelector('input[type="search"]');
                    if (input) {
                        input.focus();
                    }
                });
            }, 100);
        },

        closeModal() {
            this.showSearch = false;
            this.searchQuery = '';
            document.body.classList.remove('body--is-locked');

            // Dispose of the instantsearch instance to fully reset state
            if (this.search) {
                this.search.dispose();
                this.search = null;
            }
        },
    })
);
